import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, TextField, MenuItem, Button } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import TinkerLabListAPIs from "utilities/api/TinkerLabListAPIs";
import Auth_API from "../../utilities/api/auth_api";
import BackgroundImage from "../../assets/background.png";
import Logo1 from "../../utilities/dummy_assets/MBF_Logo.png";
import mapITlogo from "../../utilities/dummy_assets/svgs/MapIt_Logo.svg";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PageTransition from "components/PageTransition/PageTransition";

// Academic year options
const currentYears = [
  { code: 1, name: "First" },
  { code: 2, name: "Second" },
  { code: 3, name: "Third" },
  { code: 4, name: "Fourth" },
  { code: 5, name: "Fifth" },
];

const current_Year = new Date().getFullYear();

// Degree options
const degrees = [
  { code: "B.Tech", name: "B.Tech" },
  { code: "B.E", name: "B.E" },
  { code: "M.Tech", name: "M.Tech" },
  { code: "M.Sc", name: "M.Sc" },
  { code: "PhD", name: "PhD" },
  { code: "MCA", name: "MCA" },
  { code: "MBA", name: "MBA" },
  { code: "Other", name: "Other" },
];

// Branch/Department options
const branches = [
  { code: "CSE", name: "Computer Science and Engineering" },
  { code: "ECE", name: "Electronics and Communication Engineering" },
  { code: "ME", name: "Mechanical Engineering" },
  { code: "CE", name: "Civil Engineering" },
  { code: "EE", name: "Electrical Engineering" },
  { code: "CHE", name: "Chemical Engineering" },
  { code: "MME", name: "Metallurgical and Materials Engineering" },
  { code: "BT", name: "Biotechnology" },
  { code: "CL", name: "Chemistry" },
  { code: "MA", name: "Mathematics" },
  { code: "PH", name: "Physics" },
  { code: "HSS", name: "Humanities and Social Sciences" },
  { code: "MC", name: "Mathematics and Computing" },
  { code: "MSE", name: "Materials Engineering" },
  { code: "AR", name: "Automation and Robotics" },
  { code: "IT", name: "Information Technology" },
  { code: "AE", name: "Aerospace Engineering" },
  { code: "Other", name: "Other" },
];

/**
 * MoreDetail Component
 * Collects additional student information for registration
 * Includes: Student ID, academic details (year, graduation, branch, degree), and TL assignment
 */
const MoreDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Form field states
  const [studentId, setStudentId] = useState("");
  const [currentYear, setCurrentYear] = useState("");
  const [graduationYear, setGraduationYear] = useState(null);
  const [branch, setBranch] = useState("");
  const [degree, setDegree] = useState("");
  const [tl, setTl] = useState("");
  const [tinkerLabsList, setTinkerLabsList] = useState([]);

  // UI states
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    studentId: false,
    currentYear: false,
    graduationYear: false,
    branch: false,
    degree: false,
    tl: false,
  });
  const [isRequiredError, setIsRequiredError] = useState(false);

  // Fetch available Tinker Labs on component mount
  useEffect(() => {
    const fetchTinkerLabs = async () => {
      try {
        const response = await TinkerLabListAPIs.TinkerLabsListGet();
        setTinkerLabsList(response || []);
      } catch (error) {
        console.error("Failed to fetch tinker labs:", error);
        toast.error("Failed to load TL options. Please refresh the page.");
      }
    };
    fetchTinkerLabs();
  }, []);

  // Initialize TL from previous page if provided
  useEffect(() => {
    if (location.state?.tl_name && location.state.tl_name !== "") {
      setTl(location.state.tl_name.tl_id);
    }
  }, [location.state]);

  const isStudentIdRequired = useMemo(
    () =>
      !!tinkerLabsList?.find((i) => i.name === location.state?.tl_name?.tl_name)
        ?.is_student_id_reqd,
    [location?.state, tinkerLabsList]
  );

  /**
   * Validate graduation year based on current year
   * Returns true if valid, false otherwise
   */
  const validateGraduationYear = (current, grad) => {
    const currentYearNum =
      currentYears.find((y) => y.code === parseInt(current))?.code || 1;
    const minGradYear = current_Year + (5 - currentYearNum); // Assuming max 5 years from current year
    return grad >= current_Year && grad >= minGradYear;
  };

  //Filtering tht TL as per email Domain
  const studentEmail = location.state?.email || "";

  const filteredTLs = tinkerLabsList.filter(
    (lab) =>
      lab.name !== "MBF Admin" &&
      lab.accepted_ldaps.length > 0 &&
      lab.accepted_ldaps.some((domain) => studentEmail.endsWith(domain))
  );

  // Auto-select and disable TL
  useEffect(() => {
    if (filteredTLs.length === 1) {
      setTl(filteredTLs[0].id);
    }
  }, [filteredTLs]);

  /**
   * Handle form submission
   * Validates all fields before submitting registration request
   * Shows success toast and navigates to login on successful registration
   */
  const handleSubmit = async () => {
    // Clear previous errors
    setErrors({
      studentId: false,
      currentYear: false,
      graduationYear: false,
      branch: false,
      degree: false,
      tl: false,
    });
    setIsRequiredError(false);

    // Validate that all required fields are filled
    const newErrors = {
      currentYear: !currentYear,
      graduationYear: !graduationYear,
      branch: !branch,
      degree: !degree,
      tl: !tl,
    };

    // Only validate studentId if required
    if (isStudentIdRequired) {
      newErrors.studentId = !studentId;
    }

    // Validate graduation year
    if (
      graduationYear &&
      currentYear &&
      !validateGraduationYear(currentYear, graduationYear)
    ) {
      newErrors.graduationYear = true;
      toast.error("Please select a valid graduation year.");
    }

    setErrors(newErrors);

    // Stop submission if any field is empty or graduation year is invalid
    const hasError = Object.values(newErrors).some(Boolean);
    if (hasError) {
      setIsRequiredError(true);
      return;
    }

    setLoading(true);

    try {
      // Combine data from previous page (AdminOrgDetail) with current form
      const values = {
        ...location.state,
        current_year: currentYear,
        graduation_year: graduationYear ? graduationYear.year() : "",
        branch,
        degree,
        TL: tl,
      };

      if (isStudentIdRequired) {
        values.student_id = studentId;
      }

      const response = await Auth_API.studentRegister(values);

      // Handle successful registration
      if (response && (response.status === 200 || response.status === 201)) {
        toast.success(
          "Registration successful! Change your password. old password is sent on email."
        );
        navigate("/login");
        return;
      }

      // Handle unexpected response format
      const errorMessage =
        response?.data?.message || "Registration failed. Please try again.";
      throw new Error(errorMessage);
    } catch (err) {
      // Handle server validation errors (field-specific)
      if (err.response?.data && typeof err.response.data === "object") {
        const serverErrors = err.response.data;
        const mappedErrors = {
          studentId: false,
          currentYear: false,
          graduationYear: false,
          branch: false,
          degree: false,
          tl: false,
        };

        Object.keys(serverErrors).forEach((field) => {
          switch (field) {
            case "student_id":
              mappedErrors.studentId = true;
              break;
            case "current_year":
              mappedErrors.currentYear = true;
              break;
            case "graduation_year":
              mappedErrors.graduationYear = true;
              break;
            case "branch":
              mappedErrors.branch = true;
              break;
            case "degree":
              mappedErrors.degree = true;
              break;
            case "TL":
              mappedErrors.tl = true;
              break;
            default:
            // No action needed for unrecognized fields
          }
        });

        setErrors(mappedErrors);
        Object.values(serverErrors).forEach((msg) => {
          const message = Array.isArray(msg) ? msg[0] : msg;
          toast.error(message);
        });
      } else {
        // Generic error
        let errorMessage = "An error occurred during registration";
        if (err.response) {
          errorMessage =
            err.response.data?.message ||
            err.response.statusText ||
            errorMessage;
        } else if (err.request) {
          errorMessage =
            "No response from server. Please check your connection.";
        } else if (err.message) {
          errorMessage = err.message;
        }
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper to clear error on change
  const clearErrorOnChange = (field) => (event) => {
    const value = event.target.value;
    switch (field) {
      case "studentId":
        setStudentId(value);
        break;
      case "currentYear":
        setCurrentYear(value);
        break;
      case "graduationYear":
        setGraduationYear(value);
        break;
      case "branch":
        setBranch(value);
        break;
      case "degree":
        setDegree(value);
        break;
      case "tl":
        setTl(value);
        break;
    }
    setErrors({ ...errors, [field]: false });
  };

  return (
    <Box
      sx={{
        fontFamily: "Roboto",
        width: "100vw",
        height: "100vh",
        overflowY: "auto",
        boxSizing: "border-box",
        backgroundColor: { xs: "#FFFFFF", sm: "transparent" },
        backgroundImage: { xs: "none", sm: `url(${BackgroundImage})` },
        backgroundSize: "cover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        px: 2,
        py: 4,
      }}
    >
      {/* Top Left Logo */}
      <Box sx={{ position: "absolute", top: { xs: 10, sm: 24 }, left: { xs: 10, sm: 32 } }}>
        <img
          src={Logo1}
          alt="MBF Logo"
          style={{ height: "auto", width: "auto", maxHeight: "50px" }}
        />
      </Box>

      {/* Top Right Link */}
      <Box sx={{ position: "absolute", top: { xs: 10, sm: 24 }, right: { xs: 10, sm: 32 } }}>
        <Typography
          component="a"
          href={process.env.REACT_APP_ADMIN_SITE_BASE_URL}
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            color: "#007BFF",
            textDecoration: "underline",
            fontWeight: 500,
          }}
        >
          Go to Admin site
        </Typography>
      </Box>

      {/* Main Card */}
      <PageTransition
        sx={{
          width: "100%",
          maxWidth: "650px",
          background: "#FFFFFF",
          borderRadius: "24px",
          padding: "32px",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          {isStudentIdRequired && (
            <Box sx={{ flex: "1 1 48%" }}>
              {errors.studentId && (
                <Typography
                  sx={{ fontSize: "12px", color: "#d32f2f", mb: 0.5 }}
                >
                  Student ID is required
                </Typography>
              )}
              <TextField
                fullWidth
                required
                label="Student ID"
                placeholder="ghyt#5467"
                value={studentId}
                onChange={clearErrorOnChange("studentId")}
                error={errors.studentId}
              />
            </Box>
          )}
          <Box sx={{ flex: "1 1 48%" }}>
            {errors.currentYear && isRequiredError && (
              <Typography sx={{ fontSize: "12px", color: "#d32f2f", mb: 0.5 }}>
                Current Year is required
              </Typography>
            )}
            <TextField
              fullWidth
              required
              select
              label="Current Year"
              value={currentYear}
              onChange={clearErrorOnChange("currentYear")}
              error={errors.currentYear}
            >
              {currentYears.map((year) => (
                <MenuItem key={year.code} value={year.code}>
                  {year.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: "1 1 48%" }}>
            {errors.graduationYear && isRequiredError && (
              <Typography sx={{ fontSize: "12px", color: "#d32f2f", mb: 0.5 }}>
                Graduation Year is required
              </Typography>
            )}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Graduation Year"
                views={["year"]}
                value={graduationYear}
                onChange={(newValue) => {
                  setGraduationYear(newValue);
                  const newGradYear = newValue?.year();
                  if (
                    currentYear &&
                    newGradYear &&
                    !validateGraduationYear(currentYear, newGradYear)
                  ) {
                    setErrors({ ...errors, graduationYear: true });
                    toast.error("Please select a valid graduation year.");
                  } else {
                    setErrors({ ...errors, graduationYear: false });
                  }
                }}
                minDate={dayjs().year(current_Year)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    required
                    error={errors.graduationYear}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ flex: "1 1 48%" }}>
            {errors.branch && isRequiredError && (
              <Typography sx={{ fontSize: "12px", color: "#d32f2f", mb: 0.5 }}>
                Branch is required
              </Typography>
            )}
            <TextField
              fullWidth
              required
              select
              label="Branch"
              value={branch}
              onChange={clearErrorOnChange("branch")}
              error={errors.branch}
            >
              {branches.map((branch) => (
                <MenuItem key={branch.code} value={branch.code}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: "1 1 48%" }}>
            {errors.degree && isRequiredError && (
              <Typography sx={{ fontSize: "12px", color: "#d32f2f", mb: 0.5 }}>
                Degree is required
              </Typography>
            )}
            <TextField
              fullWidth
              required
              select
              label="Degree"
              value={degree}
              onChange={clearErrorOnChange("degree")}
              error={errors.degree}
            >
              {degrees.map((deg) => (
                <MenuItem key={deg.code} value={deg.code}>
                  {deg.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: "1 1 48%" }}>
            {errors.tl && isRequiredError && (
              <Typography sx={{ fontSize: "12px", color: "#d32f2f", mb: 0.5 }}>
                TL is required
              </Typography>
            )}
            <TextField
              select
              fullWidth
              label="TL"
              value={tl}
              onChange={clearErrorOnChange("tl")}
              required
              disabled={filteredTLs.length === 1}
              error={errors.tl}
              sx={{
                textAlign: "left",
              }}
            >
            
              {filteredTLs.map((lab) => (
                <MenuItem key={lab.id} value={lab.id}>
                  {lab.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {/* <TextField
            fullWidth
            select
            label="TL Zone"
            sx={{ flex: '1 1 48%' }}
          >
            {tlZones.map((zone) => (
              <MenuItem key={zone} value={zone}>{zone}</MenuItem>
            ))}
          </TextField> */}
        </Box>

        {/* Consent Checkbox */}
        {/* <FormControlLabel
          control={<Checkbox defaultChecked />}
          label={
            <Typography sx={{ fontSize: '14px', color: '#5C5C5C' }}>
              By using the MBF MIS, you consent to the processing of your information.
            </Typography>
          }
          sx={{ alignSelf: 'flex-start' }}
        /> */}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", width: "100%", gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            sx={{ borderRadius: "12px" }}
            onClick={() => navigate("/login")}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#007BFF",
              color: "#fff",
              borderRadius: "12px",
              "&:hover": { backgroundColor: "#005FCC" },
            }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Registering..." : "Register"}
          </Button>
        </Box>

        <Typography sx={{ fontSize: "14px", color: "#3F3F3F" }}>
          Already have an account?{" "}
          <a
            href="/login"
            style={{ color: "#007BFF", textDecoration: "underline" }}
          >
            Log in here
          </a>
        </Typography>
      </PageTransition>

      {/* Powered by Section */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.3rem",
          padding: { xs: "8px", sm: "12px" },
          backgroundColor: { xs: "#FFFFFF", sm: "transparent" },
        }}
      >
        <Typography fontSize={{ xs: "11px", sm: "12px" }} color="#666">
          Powered by
        </Typography>
        <img src={mapITlogo} alt="MapIT Logo" style={{ height: "30px" }} />
      </Box>
    </Box>
  );
};

export default MoreDetail;
